import { Component, OnInit, ViewChild } from '@angular/core';
import { EMCType } from '../../../enums/emc-type';
import { SortType } from '../../../models/sortType.model';
import { ChecklistItem } from '../../../models/checklist-item.model';
import { TableListPage } from '../../../models/list-page.model';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';
import { SectionDefinitionEntityType } from '../../../models/section-definition.model';
import { EntityModelCustomizationService } from '../../../services/entity-model-customization.service';
import { Purpose } from '../../../enums/purpose';
import { EmcOptions } from '../../../models/entity-model-customization.model';
import { EquipmentFilter } from '../../../models/equipment-filter.model';
import { EmcSearchOutput } from '../../../models/emc-search.model';
import { DynamicCardsComponent } from '../../entity-model-customization/dynamic-cards/dynamic-cards.component';
import { UtilitySharedService } from '../../../services/shared/utility-shared.service';
import { EquipmentListService } from '../../../services/equipment-list.service';

@Component({
  selector: 'lib-cim-equipment-list',
  templateUrl: './cim-equipment-list.component.html',
  styleUrl: './cim-equipment-list.component.scss',
  standalone: false,
})
export class CimEquipmentListComponent implements OnInit {
  @ViewChild('dynamicCards') dynamicCards!: DynamicCardsComponent;

  cimClass?: number;
  utilityId: number = 0;
  title: string = '';

  entityType: EMCType = EMCType.EquipmentTemplate;

  mapSortBy: Record<number, string> = {
    [SortType.NEWEST]: 'Newest',
    [SortType.ID_ASC]: 'ID Asc',
    [SortType.ID_DESC]: 'ID Desc',
  };

  mapFilterValues: Record<string, string> = {
    'First Captured User': 'COMMON.USER.TYPE.FIRST_CAPTURED',
    'Last Captured User': 'COMMON.USER.TYPE.LAST_CAPTURED',
    'Reassigned By': 'COMMON.USER.TYPE.REASSIGNED',
    'Validated By': 'COMMON.USER.TYPE.VALIDATED',
    'Audited By': 'COMMON.USER.TYPE.AUDITED',
    Pending: 'COMMON.ASSET_STATUS.PENDING',
    Complete: 'COMMON.ASSET_STATUS.COMPLETE',
    Rejected: 'COMMON.ASSET_STATUS.REJECTED',
    Assigned: 'COMMON.ASSET_STATUS.ASSIGNED',
    'Last Update Time': 'COMMON.DATE_TYPE.LAST_UPDATE',
    'Tagged Date': 'COMMON.DATE_TYPE.TAGGED',
    'Validated Date': 'COMMON.DATE_TYPE.VALIDATED',
    'Audited Date': 'COMMON.MBC_DETAILS.AUDITED_DATE',
    ReviewDate: 'COMMON.MBC_DETAILS.REVIEW_DATE',
  };

  mapFilterLabels: Record<string, string> = {
    Search: 'COMMON.SEARCH',
    'Filter By': 'COMMON.FILTER_BY',
    'User Type': 'COMMON.USER.TYPE.TITLE',
    Type: 'COMMON.TYPE',
    Status: 'COMMON.STATUS',
    'Date From': 'COMMON.DATE_FROM',
    'Date To': 'COMMON.DATE_TO',
    'Date Type': 'COMMON.DATE_TYPE.TITLE',
    Area: 'COMMON.UT.SINGLE',
    User: 'COMMON.USER.SINGLE',
  };

  cimClassLabels: Record<number, string> = {
    1: 'Power Transformer',
    2: 'AC Line Segment',
    3: 'Switch Gear',
    4: 'Conductor',
    5: 'Busbar',
    6: 'Junction',
    7: 'Jumper',
    8: 'Fuse',
    9: 'Disconnector',
    10: 'Ground Disconnector',
    11: 'Load Break Switch',
    12: 'Breaker',
    13: 'Ground',
    14: 'Recloser',
    15: 'Cut',
    16: 'Sectionaliser',
    17: 'Petersen Coil',
    18: 'Shunt Compensator',
    19: 'Frequency Converter',
    20: 'Static Var Compensator',
  };

  count: number = 0;
  filter: EquipmentFilter = new EquipmentFilter();
  isFilterWindowOpened: boolean = false;
  isDynamicFilterWindowOpened: boolean = false;
  checklistItems: ChecklistItem[] = [];
  tableListPage: TableListPage = new TableListPage();
  emcOptions: EmcOptions[] = [];

  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  constructor(
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private equipmentListService: EquipmentListService,
    private router: Router,
    private emcService: EntityModelCustomizationService,
    private utilitySharedService: UtilitySharedService,
  ) {
    this.utilityId = this.utilitySharedService.getUtilityId() ?? 0;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const cimClassParam = params.get('cimClass');
      this.cimClass = cimClassParam ? Number(cimClassParam) : undefined;
      this.title =
        this.cimClass != null
          ? (this.cimClassLabels[this.cimClass] ?? 'Equipment')
          : 'All Equipment';

      this.filter = new EquipmentFilter(this.utilityId, this.cimClass ?? undefined);
      this.checklistItems = [];

      const cacheKey = `filterDynamicEquipment|${this.cimClass ?? 'all'}`;
      const cachedFilters = localStorage.getItem(cacheKey);
      if (cachedFilters) {
        const parsed = JSON.parse(cachedFilters);
        this.filter = parsed;
        this.filter.cimClass = this.cimClass;
        this.filter.filterParams.statuses = parsed.filterParams.statuses ?? [];
        this.createFilterCards();
        localStorage.removeItem(cacheKey);
      }

      this.getCardEmcOptions(this.utilityId);
      this.onPageChange(this.filter.pageInfo.page);
    });
  }

  private getCardEmcOptions(utilityId: number) {
    this.emcService
      .getSelectedEmcOptions(
        SectionDefinitionEntityType.EquipmentTemplate,
        Purpose.Card,
        utilityId,
        0,
        this.cimClass ?? undefined,
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.emcOptions = responseData.data ?? [];
          } else {
            this.toastr.error(responseData?.message || 'Failed to fetch EMC options');
          }
        },
        error: () => {
          this.toastr.error('Error occurred while fetching EMC options');
        },
      });
  }

  onPageChange(currentPage: number) {
    this.filter.pageInfo.page = currentPage;
    this.reloadTable(currentPage);
  }

  reloadTable(page: number | null = null) {
    if (page) this.filter.pageInfo.page = page;
    this.getAllDynamic(this.filter);
  }

  private getAllDynamic(filter: EquipmentFilter) {
    this.equipmentListService.getAllDynamic(filter).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        this.tableListPage = responseData?.data ?? new TableListPage();
      },
      error: () => this.toastr.error('Error occurred while loading equipment'),
    });
  }

  onRowClick(guid: string) {
    const cacheKey = `filterDynamicEquipment|${this.cimClass ?? 'all'}`;
    localStorage.setItem(cacheKey, JSON.stringify(this.filter));
    this.router.navigate(['edit', guid], { relativeTo: this.route });
  }

  onDynamicFilterWindowChange(val: boolean) {
    this.isDynamicFilterWindowOpened = val;
  }

  closeDynamicFilterWindow() {
    this.isDynamicFilterWindowOpened = false;
  }

  onFilterWindowChange(val: boolean) {
    this.isFilterWindowOpened = val;
  }

  closeFilterWindow() {
    this.isFilterWindowOpened = false;
  }

  onSortTypeChange(option: SortType) {
    this.filter.filterParams.sortBy = option;
    this.filter.filterParams.sortByName = this.mapSortBy[option];
    this.reloadTable();
  }

  onAddFilterCard(data: ChecklistItem) {
    this.filter.searchObject.searchValue = data.selectedValue;
    this.createFilterCards();
  }

  onAddFilter() {
    this.createFilterCards();
  }

  applyFilter() {
    this.reloadTable(1);
    this.dynamicCards?.refresh();
  }

  removeFilter(checklistItem: ChecklistItem) {
    if (checklistItem.property === 'Date Type') return;

    const filter = this.checklistItems.find(
      (f) =>
        f.property === checklistItem.property &&
        f.selectedValue === checklistItem.selectedValue,
    );

    if (filter) {
      this.checklistItems = this.checklistItems.filter((f) => f !== filter);
      this.updateFilterFromRemovedItem(filter);
      this.createFilterCards();
      if (this.checklistItems.length === 0) {
        this.applyFilter();
      }
    }
  }

  searchfilter(obj: any) {
    this.filter.searchObject = obj;
  }

  download() {
    this.equipmentListService.downloadDynamic(this.filter).subscribe({
      next: (response: any) => {
        const data = response;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  resetFilter() {
    this.filter = new EquipmentFilter(this.utilityId, this.cimClass ?? undefined);
    this.checklistItems = [];
    this.onPageChange(1);
  }

  private updateFilterFromRemovedItem(item: ChecklistItem) {
    const staticFields = this.filter.filterParams;
    switch (item.property) {
      case 'Search':
        this.filter.searchObject = new EmcSearchOutput();
        break;
      case 'Status':
        staticFields.status = null;
        break;
      case 'Area':
        staticFields.selectedAreaModels = staticFields.selectedAreaModels.filter(
          (a) => a.name !== item.selectedValue,
        );
        break;
      case 'User':
        staticFields.selectedUserModels = staticFields.selectedUserModels.filter(
          (u) => u.name !== item.selectedValue,
        );
        break;
      case 'Date From':
        staticFields.dateFrom = null;
        break;
      case 'Date To':
        staticFields.dateTo = null;
        break;
      default:
        const dfIndex = this.filter.dynamicFilters.findIndex((d) =>
          item.property.startsWith(d.attributeLabel),
        );
        if (dfIndex !== -1) {
          const df = this.filter.dynamicFilters[dfIndex];
          if (item.property.endsWith('From')) {
            df.from = null;
          } else if (item.property.endsWith('To')) {
            df.to = null;
          } else {
            if (df.selectedOptions && df.optionDefinitions) {
              const option = df.optionDefinitions.find(
                (opt) => opt.optionValue === item.selectedValue,
              );
              if (option) {
                df.selectedOptions = df.selectedOptions.filter(
                  (id) => id !== option.optionId,
                );
              }
            }
            df.optionDefinitions = df.optionDefinitions?.filter(
              (opt) => opt.optionValue !== item.selectedValue,
            );
          }
          const hasAny =
            (df.selectedOptions?.length ?? 0) > 0 ||
            df.from != null ||
            df.to != null;
          if (!hasAny) this.filter.dynamicFilters.splice(dfIndex, 1);
        }
        break;
    }
  }

  private createFilterCards() {
    const items: ChecklistItem[] = [];
    const staticFields = this.filter.filterParams;

    if (this.filter.searchObject.searchValue) {
      items.push(
        new ChecklistItem(
          'Search',
          this.filter.searchObject.searchValue,
          this.mapFilterLabels['Search'],
        ),
      );
    }

    staticFields.selectedAreaModels.forEach((area) => {
      items.push(
        new ChecklistItem('Area', area.name, this.mapFilterLabels['Area']),
      );
    });

    staticFields.selectedUserModels.forEach((user) => {
      items.push(
        new ChecklistItem('User', user.name, this.mapFilterLabels['User']),
      );
    });

    if (staticFields.dateFrom) {
      items.push(
        new ChecklistItem(
          'Date From',
          staticFields.dateFrom,
          this.mapFilterLabels['Date From'],
        ),
      );
    }
    if (staticFields.dateTo) {
      items.push(
        new ChecklistItem(
          'Date To',
          staticFields.dateTo,
          this.mapFilterLabels['Date To'],
        ),
      );
    }
    if ((staticFields.dateFrom || staticFields.dateTo) && staticFields.dateType) {
      items.push(
        new ChecklistItem(
          'Date Type',
          staticFields.dateTypeString,
          this.mapFilterLabels['Date Type'],
        ),
      );
    }

    this.filter.dynamicFilters.forEach((df) => {
      df.optionDefinitions?.forEach((option) => {
        items.push(new ChecklistItem(option.attributeLabel, option.optionValue));
      });
      if (df.from) {
        items.push(
          new ChecklistItem(
            df.attributeLabel + ' From',
            df.from.toLocaleDateString(),
          ),
        );
      }
      if (df.to) {
        items.push(
          new ChecklistItem(
            df.attributeLabel + ' To',
            df.to.toLocaleDateString(),
          ),
        );
      }
    });

    this.checklistItems = items;
  }
}
